// mobile-shell.ts — the "C" mobile layout. A phone-only reorganization of the REAL app: a PERSISTENT
// game board pinned to the top, a rotating bottom (three panels), and a ⚙ Settings overlay for
// everything else. It moves the existing, already-wired DOM nodes (no rebuild, anti-drift) — every
// listener, effect, and control keeps working. Desktop is never touched: main.ts only calls this on
// a phone (or when ?c is forced).
//
//   ┌─ #mtop  — PERSISTENT board: shrunk DULY NOTED title · ⚙ · word display · type-a-word + Submit
//   ├─ #mshell — rotating bottom (swipe or tap): [Personality] · [HOME = Start Mashing + text] · [Juice]
//   ├─ #mtabs — the three tabs
//   └─ #msettings — full-screen overlay: version · variant switcher (A/B/C/Desktop) · swept cruft
//
// Laid out as a CSS grid (auto board / 1fr carousel / tabs). Each bottom panel has a fixed full-height
// backing, so they never jump; the board's internals are fixed-height, so its `auto` track is locked.

/** Phone layout target: narrow screens, or a coarse-pointer device up to a landscape phone width.
 *  `?c` forces it on any width; `?desktop` (or `?nc`) forces the desktop layout even on a phone —
 *  both drive the Settings "variant switcher", and mirror the existing `?ab=` / `?dict=` convention. */
export function isPhoneLayout(): boolean {
  const params = new URLSearchParams(location.search);
  if (params.has("desktop") || params.has("nc")) return false;
  if (params.has("c")) return true;
  return (
    window.matchMedia("(max-width: 760px)").matches ||
    (window.matchMedia("(pointer: coarse)").matches && window.matchMedia("(max-width: 926px)").matches)
  );
}

// Bottom rotation, left→right. HOME is the landing (index 1). "More" is gone — its cruft moved to ⚙.
const TABS = [
  { key: "pers", label: "Personality" },
  { key: "home", label: "Home" },
  { key: "juice", label: "Juice" },
] as const;

// Settings "variant switcher" — each reloads the page with the flag that selects that experience.
const VARIANTS = [
  { label: "A · plain", search: "?ab=A" },
  { label: "B · tutorial", search: "?ab=B" },
  { label: "C · mobile", search: "?c=1" },
  { label: "Desktop", search: "?desktop=1" },
] as const;

function panelEl(key: string): HTMLElement {
  const el = document.createElement("section");
  el.className = "mpanel";
  el.dataset.panel = key;
  return el;
}
function heading(text: string): HTMLElement {
  const h = document.createElement("h3");
  h.className = "mpanel-h";
  h.textContent = text;
  return h;
}

export function initMobileShell(): void {
  if (document.getElementById("mshell")) return; // build once
  const q = <T extends Element>(s: string, root: ParentNode = document) => root.querySelector<T>(s);

  const stage = q<HTMLElement>("#stage");
  if (!stage) return; // nothing to panelize
  const funpanel = q<HTMLElement>(".funpanel");
  const leftRail = q<HTMLElement>(".tuner-shell.tuner-left");
  const rightRail = q<HTMLElement>(".tuner-shell.tuner-right");

  // Make sure the real nodes are in their revealed state (main.ts also does this; belt & braces).
  funpanel?.classList.remove("pre-reveal");
  leftRail?.classList.remove("pre-reveal-reserve");
  rightRail?.classList.remove("pre-reveal-reserve");

  const panels: Record<string, HTMLElement> = {};
  for (const t of TABS) panels[t.key] = panelEl(t.key);

  // ---- ⚙ SETTINGS overlay — the paper surface that holds everything off the main screen ----
  const settings = document.createElement("div");
  settings.id = "msettings";
  settings.hidden = true;
  const shead = document.createElement("div");
  shead.className = "msettings-head";
  const stitle = document.createElement("h2");
  stitle.textContent = "Settings";
  const sclose = document.createElement("button");
  sclose.className = "msettings-close";
  sclose.textContent = "✕";
  sclose.setAttribute("aria-label", "Close settings");
  shead.append(stitle, sclose);
  settings.append(shead);

  // Hard reload — the standout utility action. Encoded as a solid, full-width button (vs the outlined
  // controls around it) at the top of Settings, so it reads as the primary action by form, not by
  // hiding anything. Reload-only: replays the intro from the top; saved prefs/arm are kept.
  const reload = document.createElement("button");
  reload.className = "msettings-reload";
  reload.textContent = "↻ Restart from scratch";
  reload.addEventListener("click", () => location.reload());
  settings.append(reload);

  const section = (title: string, ...nodes: (Node | null | undefined)[]): HTMLElement => {
    const sec = document.createElement("div");
    sec.className = "msettings-sec";
    sec.append(heading(title));
    for (const n of nodes) if (n) sec.append(n);
    return sec;
  };

  // Variant switcher (A / B / C / Desktop) — reloads with the selecting flag.
  const variants = document.createElement("div");
  variants.className = "mvariants";
  for (const v of VARIANTS) {
    const b = document.createElement("button");
    b.textContent = v.label;
    b.addEventListener("click", () => {
      location.href = location.pathname + v.search;
    });
    variants.append(b);
  }
  settings.append(section("Load variant", variants));

  // Version (the real dropdown + build notes) moves here; drop the redundant A|B pill (switcher covers it).
  const verSlot = q<HTMLElement>(".version-slot", stage);
  q<HTMLElement>(".ab-toggle")?.remove();
  if (verSlot) settings.append(section("Build", verSlot));

  // Swept cruft — readouts, controls, export (the overlay is paper, so they read as designed).
  const readouts = document.createElement("div");
  for (const sel of [".rule", ".patience", ".ladder", "#status"]) {
    const n = q<HTMLElement>(sel, stage);
    if (n) readouts.append(n);
  }
  if (readouts.childElementCount) settings.append(section("Compliance readouts", readouts));
  const footer = q<HTMLElement>(".rig-readout", stage);
  if (footer) settings.append(section("Controls", footer));
  const exportBtn = funpanel && q<HTMLElement>(".funpanel-export", funpanel);
  const exportJson = funpanel && q<HTMLElement>(".funpanel-json", funpanel);
  if (exportBtn || exportJson) settings.append(section("Export", exportBtn, exportJson));

  // ---- HOME — the Start Mashing widget, with its how-to text below the buttons. The blurb's
  //      desktop pointer is reworded for the mobile swipe (C-only edit; desktop copy untouched).
  if (funpanel) {
    const hint = q<HTMLElement>(".funpanel-hint", funpanel);
    if (hint) hint.textContent = "Big, impressive fires — goof here, then swipe ‹ Personality · Juice › to tune.";
    panels.home.append(funpanel);
  }

  // ---- PERSONALITY / JUICE — the rails, refit into the bottom area (their intro copy rides along). ----
  if (leftRail) panels.pers.append(leftRail);
  if (rightRail) panels.juice.append(rightRail);

  // ---- PERSISTENT BOARD — the stripped #stage: shrunk title + ⚙ + word display + type field + Submit.
  const form = q<HTMLFormElement>("#submit-form", stage);
  if (form && !q(".cw-submit", form)) {
    const submitBtn = document.createElement("button");
    submitBtn.type = "submit"; // Enter still works; this is a real submit button
    submitBtn.className = "cw-submit";
    submitBtn.textContent = "Submit";
    form.append(submitBtn);
  }
  const gear = document.createElement("button");
  gear.className = "m-gear";
  gear.textContent = "⚙";
  gear.setAttribute("aria-label", "Settings");
  q<HTMLElement>(".topbar", stage)?.append(gear); // right side of the board title (version-slot vacated)
  const top = document.createElement("div");
  top.id = "mtop";
  top.append(stage);

  // settings open/close
  gear.addEventListener("click", () => (settings.hidden = false));
  sclose.addEventListener("click", () => (settings.hidden = true));

  // ---- assemble: board (row 1) · carousel (row 2) · tabs (row 3) · settings overlay ----
  // The carousel is a transform-based SLIDER, not a scroll container — so it never competes with each
  // panel's own vertical scroll on touch (a horizontal scroll-snap parent swallows vertical drags on
  // iOS). #mshell just clips; #mtrack slides via translateX; each .mpanel scrolls vertically natively.
  const shell = document.createElement("div");
  shell.id = "mshell";
  TABS.forEach((t, i) => {
    panels[t.key].style.setProperty("--i", String(i));
    shell.append(panels[t.key]);
  });

  const tabbar = document.createElement("nav");
  tabbar.id = "mtabs";
  const tabBtns: Record<string, HTMLButtonElement> = {};
  for (const t of TABS) {
    const b = document.createElement("button");
    b.textContent = t.label;
    b.dataset.go = t.key;
    tabBtns[t.key] = b;
    tabbar.append(b);
  }

  document.body.append(top, shell, tabbar, settings);
  document.body.classList.add("m-shell");

  // ---- navigation: tap a tab OR swipe horizontally; vertical drags scroll the panel natively ----
  const keys = TABS.map((t) => t.key);
  let idx = Math.max(0, keys.indexOf("home"));
  const setActive = (i: number) => {
    for (let k = 0; k < TABS.length; k++) tabBtns[TABS[k].key].classList.toggle("on", k === i);
  };
  const goTo = (i: number) => {
    idx = Math.max(0, Math.min(keys.length - 1, i));
    shell.style.setProperty("--active", String(idx));
    setActive(idx);
  };
  for (let k = 0; k < TABS.length; k++) tabBtns[TABS[k].key].addEventListener("click", () => goTo(k));

  // Horizontal swipe → change panel. `touch-action: pan-y` on #mshell (CSS) lets the browser keep the
  // vertical scroll native while leaving horizontal gestures to us, so the two never fight.
  let x0 = 0;
  let y0 = 0;
  shell.addEventListener(
    "touchstart",
    (e) => {
      x0 = e.changedTouches[0].clientX;
      y0 = e.changedTouches[0].clientY;
    },
    { passive: true },
  );
  shell.addEventListener(
    "touchend",
    (e) => {
      const dx = e.changedTouches[0].clientX - x0;
      const dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > Math.abs(dy) * 1.3 && Math.abs(dx) > 40) goTo(idx + (dx < 0 ? 1 : -1));
    },
    { passive: true },
  );

  // Land on HOME with no opening animation, then re-enable the slide transition.
  shell.classList.add("m-noanim");
  goTo(idx);
  requestAnimationFrame(() => {
    shell.classList.remove("m-noanim");
    // Lock the rail boxes to Start Mashing's height so Personality/Juice match it (measured once).
    if (funpanel) shell.style.setProperty("--mbox-h", funpanel.offsetHeight + "px");
  });
}
